import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, Radio, Row, Select, Upload, Table, Space } from "antd";

import { useAsyncAxios, utilAxiosFormWithAuth, utilAxiosWithAppToken } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import { UploadOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { DownloadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";

import * as String from "../commons/string";
import * as Types from "../commons/typeDTO";
import * as Request from "../commons/request";
import * as Common from "../commons/common";

import BBSEditor from "./BBSEditor";
import moment from "moment";

const formItemLayout = {
    labelCol: {
        sm: {
            span: 5,
        },
    },
    wrapperCol: {
        sm: {
            span: 19,
        },
    },
};

function RentRegisterNotice({
    editMode,
    rentNotice,
    onRegisterRentNotice,
}: {
    editMode: string;
    rentNotice?: Types.RentNoticeDto;
    onRegisterRentNotice?: () => void;
}) {
    const [form] = Form.useForm();
    const alert = useAlert();
    const [selectedRentNotice, setSelectedRentNotice] = useState<Types.RentNoticeDto>();
    const [attachFiles, setAttachFiles] = useState<Array<any>>([]);
    const [noticeType, setNoticeType] = useState<string>("");
    const [selectedRentNoticeFiles, setSelectedRentNoticeFiles] = useState<Array<Types.RentNoticeFileDto>>();
    const noticeTypes = [
        { value: "NOTICE", label: "공지사항" },
        { value: "EVENT", label: "이벤트" },
        { value: "EVENT_ANNOUNCEMENT", label: "이벤트 발표" },
    ];

    useEffect(() => {
        if (selectedRentNotice === null || selectedRentNotice === undefined) return;

        setNoticeType(selectedRentNotice.noticeType);
        form.setFieldsValue(selectedRentNotice);
        file(selectedRentNotice.rentNoticeFiles);
    }, [selectedRentNotice]);

    useEffect(() => {
        if (rentNotice === undefined) return;
        requestGetRentNotice(rentNotice.rentNoticeId);
    }, [rentNotice]);

    useEffect(() => {
        if (noticeType === "NOTICE" || noticeType === "EVENT_ANNOUNCEMENT") {
            form.setFieldValue("noticeStatus", "NOTICE");
        }
        if (noticeType === "EVENT") {
            form.setFieldValue("noticeStatus", "EVENT_IN_PROGRESS");
        }
    }, [noticeType]);

    const columns: ColumnsType<Types.RentNoticeFileDto> = [
        {
            align: "center",
            title: String.noticeFileId,
            dataIndex: "rentNoticeFileId",
            key: "rentNoticeFileId",
            render: (_, { rentNoticeFileId }) => {
                return rentNoticeFileId ? <p>{rentNoticeFileId}</p> : "";
            },
        },
        {
            align: "center",
            title: String.noticeFileName,
            dataIndex: "rentNoticeFileName",
            key: "rentNoticeFileName",
            render: (_, { rentNoticeFileId, rentNoticeFileName }) => {
                return rentNoticeFileName ? (
                    <Space onClick={() => handleDownloadNoticeFile(rentNoticeFileId, rentNoticeFileName)} style={{ cursor: "pointer" }}>
                        <DownloadOutlined />
                        <p>{rentNoticeFileName}</p>
                    </Space>
                ) : (
                    ""
                );
            },
        },
    ];

    //get notice
    const requestAxiosGetRentNotice = async (noticeId: number) => {
        const response = await utilAxiosFormWithAuth().get(Request.RENT_NOTICE_URL + "/" + noticeId);

        return response.data;
    };
    const {
        loading: loadingGetRentNotice,
        error: errorGetRentNotice,
        data: resultGetRentNotice,
        execute: requestGetRentNotice,
    } = useAsyncAxios(requestAxiosGetRentNotice);

    useEffect(() => {
        if (resultGetRentNotice === null) return;

        setSelectedRentNotice(resultGetRentNotice.rentNotice);
    }, [resultGetRentNotice]);

    useEffect(() => {
        if (errorGetRentNotice === null) return;

        console.log("errorGetRentNotice", errorGetRentNotice);
        alert.setAlert(errorGetRentNotice.response.data.code, "공지사항 조회 실패", errorGetRentNotice.response.data.message);
    }, [errorGetRentNotice]);

    //add notice
    const requestAxiosAddRentNotice = async (data: Types.RentNoticeDto) => {
        const response = await utilAxiosFormWithAuth().post(Request.RENT_NOTICE_URL, data);
        return response.data;
    };

    const {
        loading: loadingAddRentNotice,
        error: errorAddRentNotice,
        data: resultAddRentNotice,
        execute: requestAddRentNotice,
    } = useAsyncAxios(requestAxiosAddRentNotice);

    useEffect(() => {
        if (resultAddRentNotice === null) return;

        if (onRegisterRentNotice) onRegisterRentNotice();
        alert.setAlert(AlertType.SUCCESS, "공지사항 추가 성공", "공지사항 추가를 완료하였습니다.");
    }, [resultAddRentNotice]);

    useEffect(() => {
        if (errorAddRentNotice === null) return;

        console.log("errorRentNotice", errorAddRentNotice);
        alert.setAlert(errorAddRentNotice.response.data.code, "공지사항 추가 실패", errorAddRentNotice.response.data.message);
    }, [errorAddRentNotice]);

    //update notice
    const requestAxiosUpdateRentNotice = async (data: any, rentNoticeId: number) => {
        const response = await utilAxiosFormWithAuth().put(Request.RENT_NOTICE_URL + "/" + rentNoticeId, data);
        return response.data;
    };

    const {
        loading: loadingUpdateRentNotice,
        error: errorUpdateRentNotice,
        data: resultUpdateRentNotice,
        execute: requestUpdateRentNotice,
    } = useAsyncAxios(requestAxiosUpdateRentNotice);

    useEffect(() => {
        if (resultUpdateRentNotice === null) return;

        if (onRegisterRentNotice) onRegisterRentNotice();

        alert.setAlert(AlertType.SUCCESS, "공지사항 수정 성공", "공지사항 수정을 완료하였습니다.");
    }, [resultUpdateRentNotice]);

    useEffect(() => {
        if (errorUpdateRentNotice === null) return;

        console.log("errorUpdateRentNotice", errorUpdateRentNotice);
        alert.setAlert(errorUpdateRentNotice.response.data.code, "공지사항 수정 실패", errorUpdateRentNotice.response.data.message);
    }, [errorUpdateRentNotice]);

    const onFinish = async (values: any) => {
        const formData = new FormData();
        delete values["rentNoticeFiles"];

        const data: Types.RentNoticeDto = {
            ...values,
            rentNoticeFiles: selectedRentNoticeFiles,
        };

        formData.append("rentNoticeDto", new Blob([JSON.stringify(data)], { type: "application/json" }));

        for (const file in attachFiles) {
            const response = await fetch(URL.createObjectURL(attachFiles[file]));
            const blob = await response.blob();
            const data = new File([blob], attachFiles[file].name, { type: blob.type });

            formData.append("rentNoticeFiles", data);
        }

        if (editMode === "add") requestAddRentNotice(formData);
        else if (editMode === "update") requestUpdateRentNotice(formData, values.rentNoticeId);
    };

    const file = (files: Types.RentNoticeFileDto[]) => {
        const data: UploadFile[] = [];
        files &&
            files.map((item: any) => {
                data.push({
                    uid: item.rentNoticeFileId,
                    name: item.rentNoticeFileName,
                });
            });
    };

    const propsAttachFiles = {
        onRemove: (file: any) => {
            const index = attachFiles.indexOf(file);
            const newAttachFiles = attachFiles.slice();
            newAttachFiles.splice(index, 1);
            setAttachFiles(newAttachFiles);
        },
        beforeUpload: (file: any) => {
            setAttachFiles([...attachFiles, file]);
            return false;
        },
        fileList: attachFiles,
    };

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: Types.RentNoticeFileDto[]) => {
            const list: Array<Types.RentNoticeFileDto> = [];
            for (let test of selectedRows) {
                test = {
                    ...test,
                    deleted: true,
                };
                list.push(test);
            }
            setSelectedRentNoticeFiles(list);
        },
    };

    const handleDownloadNoticeFile = async (rentNoticeFileId: number, rentNoticeFileName: string) => {
        await utilAxiosWithAppToken()
            .get(Request.RENT_NOTICE_FILE_DOWNLOAD_URL + "/" + rentNoticeFileId, { responseType: "blob" })
            .then((response) => {
                const file = new Blob([response.data]);
                const element = document.createElement("a");
                element.href = URL.createObjectURL(file);
                element.download = rentNoticeFileName;
                element.click();
            })
            .catch((error) => {
                console.log("downloadNoticeFile error", error);
                alert.setAlert("", "파일 다운로드 실패", "파일 다운로드에 실패했습니다.");
            });
    };

    return (
        <Form style={{ paddingTop: 20 }} form={form} onFinish={onFinish}>
            <Space size={20} direction="vertical" style={{ width: "100%" }}>
                <Row>
                    <Col span={24}>
                        <Form.Item
                            className="searchForm"
                            {...formItemLayout}
                            name="title"
                            label="제목"
                            rules={[
                                {
                                    required: true,
                                    message: "제목을 입력해 주세요.",
                                },
                            ]}
                        >
                            {editMode === "add" ? <Input placeholder={String.title} /> : <Input />}
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item className="searchForm" {...formItemLayout} name="rentNoticeId" label="ID">
                            <Input readOnly />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item className="searchForm" {...formItemLayout} name="hits" label="조회수">
                            <Input readOnly />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item className="searchForm" {...formItemLayout} name="registeredDate" label="등록일">
                            <Input defaultValue={moment().format(Common.FORMAT_DATE_TIME)} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item className="searchForm" {...formItemLayout} name="noticeType" label="카테고리">
                            <Select onChange={(value) => setNoticeType(value)} options={noticeTypes} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item className="searchForm" {...formItemLayout} name="noticeStatus" label="이벤트 상태">
                            <Select
                                disabled={noticeType !== "EVENT" ? true : false}
                                options={[
                                    { value: "EVENT_IN_PROGRESS", label: "진행" },
                                    { value: "EVENT_FINISHED", label: "종료" },
                                    { value: "NOTICE", label: "", disabled: true },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item className="searchForm" {...formItemLayout} name="fixed" label="고정">
                            <Radio.Group>
                                <Radio value={true}>○</Radio>
                                <Radio value={false}>✕</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            className="searchForm"
                            {...formItemLayout}
                            name="contents"
                            label="내용"
                            rules={[
                                {
                                    required: true,
                                    message: "내용을 입력해 주세요.",
                                },
                            ]}
                        >
                            {editMode === "add" ? <BBSEditor hasAttachFiles={false} /> : <BBSEditor />}
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item className="searchForm" {...formItemLayout} name="rentNoticeFiles" label="첨부파일">
                            <Upload {...propsAttachFiles} multiple={true}>
                                <Button icon={<UploadOutlined />}>{String.attachment}</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item className="searchForm" name="rentNoticeFiles" label="첨부파일 목록">
                            <Table
                                className="pointerTable"
                                rowSelection={{
                                    type: "checkbox",
                                    ...rowSelection,
                                }}
                                rowKey="rentNoticeFileId"
                                columns={columns}
                                dataSource={selectedRentNotice?.rentNoticeFiles}
                                pagination={{ position: ["bottomCenter"], showTotal: (total) => `총 ${total}개` }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row justify="end">
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {editMode === "add" ? String.register : String.update}
                        </Button>
                    </Form.Item>
                </Row>
            </Space>
        </Form>
    );
}

export default RentRegisterNotice;
